import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ChevronLeft, PawPrint } from "lucide-react"
import {
  archiveDog,
  deleteDog,
  fetchDog,
  fetchDogs,
  updateDog,
  type DogProfile,
} from "~/api/dogs"
import { getProfile } from "~/api/auth"
import { SearchableBreedSelector } from "~/components/SearchableBreedSelector"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { DOG_AGE_RANGE_OPTIONS } from "~/data/dogAgeRanges"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { hasEntitlement, ENTITLEMENTS } from "~/utils/entitlements"

function ageLabel(ageRange: DogProfile["age_range"]): string {
  if (!ageRange) return "Not set"
  return DOG_AGE_RANGE_OPTIONS.find((option) => option.value === ageRange)?.label ?? ageRange
}

function sexLabel(sex: DogProfile["sex"]): string {
  return sex === "female" ? "Female" : "Male"
}

export function DogDetailsPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dogId = Number(id)
  const [dog, setDog] = useState<DogProfile | null>(null)
  const [activeDogCount, setActiveDogCount] = useState(1)
  const [hasPlus, setHasPlus] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editBreed, setEditBreed] = useState("")
  const [editMixDetail, setEditMixDetail] = useState("")
  const [breedValid, setBreedValid] = useState(true)
  const [savingBreed, setSavingBreed] = useState(false)
  const [breedMessage, setBreedMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isFinite(dogId) || dogId <= 0) {
      navigate("/profile", { replace: true })
      return
    }

    Promise.all([fetchDog(dogId), fetchDogs(), getProfile()])
      .then(([dogResponse, dogsResponse, profile]) => {
        setDog(dogResponse.dog)
        setEditBreed(dogResponse.dog.breed ?? "")
        setEditMixDetail(dogResponse.dog.mix_detail ?? "")
        setActiveDogCount(dogsResponse.dogs.length)
        setHasPlus(hasEntitlement(profile.user.entitlements, ENTITLEMENTS.multipleDogs))
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Could not load dog")
      })
      .finally(() => setLoading(false))
  }, [dogId, navigate])

  async function handleSaveBreed() {
    if (!dog || savingBreed || !breedValid) return
    setSavingBreed(true)
    setBreedMessage(null)
    setError(null)
    try {
      const result = await updateDog(dog.id, {
        breed: editBreed || null,
        mix_detail: editBreed ? editMixDetail || null : null,
      })
      setDog(result.dog)
      setBreedMessage("Breed saved.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save breed")
    } finally {
      setSavingBreed(false)
    }
  }

  async function handleArchive() {
    if (!dog) return
    try {
      await archiveDog(dog.id)
      navigate("/pack/manage", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive")
    }
  }

  async function handleDelete() {
    if (!dog) return
    const confirmed = window.confirm(
      `Permanently delete ${dog.name}?\n\nThis removes ${dog.name}'s profile, Story, portraits, photos, notes/observations, saved Ask Dynamo conversations, and all associated memories. This cannot be undone.`,
    )
    if (!confirmed) return
    try {
      await deleteDog(dog.id)
      navigate("/profile", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete")
    }
  }

  if (loading) {
    return (
      <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
        <LoggedInScreenCard>
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        </LoggedInScreenCard>
      </PageShell>
    )
  }

  if (!dog) {
    return (
      <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
        <LoggedInScreenCard>
          <p className="text-sm text-red-600" role="alert">
            {error ?? "Dog not found"}
          </p>
          <Link to="/profile" className="mt-3 inline-block text-sm text-[var(--color-brand-primary)] underline">
            Back to profile
          </Link>
        </LoggedInScreenCard>
      </PageShell>
    )
  }

  const portraitSrc = heroPortraitUrl(dog.archetype_icon, dog.portrait)
  const canArchive = hasPlus && activeDogCount > 1 && !dog.plus_locked

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <header className="mb-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
          <h1 className="flex-1 truncate text-lg font-semibold text-[var(--color-text-primary)]">
            {dog.name}
          </h1>
      </header>

      <div>
        {error && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <section className="flex flex-col items-center rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-6">
          {portraitSrc ? (
            <img
              src={portraitSrc}
              alt=""
              className="h-32 w-32 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-full border border-[var(--color-divider)]">
              <PawPrint size={36} className="text-[var(--color-text-secondary)]" />
            </div>
          )}
          <h2 className="mt-4 text-2xl font-semibold text-[var(--color-text-primary)]">{dog.name}</h2>
          {dog.archetype_label && (
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{dog.archetype_label}</p>
          )}
        </section>

        <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-text-secondary)]">Sex</dt>
              <dd className="font-medium text-[var(--color-text-primary)]">{sexLabel(dog.sex)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-text-secondary)]">Age</dt>
              <dd className="font-medium text-[var(--color-text-primary)]">{ageLabel(dog.age_range)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-text-secondary)]">Breed</dt>
              <dd className="font-medium text-[var(--color-text-primary)]">
                {dog.breed || "Not set"}
                {dog.mix_detail ? ` (${dog.mix_detail})` : ""}
              </dd>
            </div>
          </dl>
        </section>

        {!dog.plus_locked && (
          <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
            <SearchableBreedSelector
              value={editBreed}
              mixDetail={editMixDetail}
              onChange={(breed, mixDetail) => {
                setEditBreed(breed)
                setEditMixDetail(mixDetail ?? "")
              }}
              onValidityChange={setBreedValid}
              placeholder="Search or select breed (optional)"
            />
            {breedMessage && (
              <p className="mt-2 text-sm text-green-700" role="status">
                {breedMessage}
              </p>
            )}
            <button
              type="button"
              disabled={savingBreed || !breedValid || (editBreed || "") === (dog.breed || "")}
              onClick={() => void handleSaveBreed()}
              className="mt-3 w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
            >
              {savingBreed ? "Saving…" : "Save breed"}
            </button>
          </section>
        )}

        <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
          <Link
            to={`/story?dog_id=${dog.id}`}
            className="block text-sm font-medium text-[var(--color-brand-primary)] underline-offset-2 hover:underline"
          >
            View {dog.name}&apos;s Story
          </Link>
        </section>

        <section className="mt-6 flex flex-wrap gap-3 text-sm">
          {canArchive && (
            <button
              type="button"
              onClick={() => void handleArchive()}
              className="text-[var(--color-text-secondary)] underline-offset-2 hover:underline"
            >
              Archive
            </button>
          )}
          {!dog.plus_locked && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              className="text-red-600 underline-offset-2 hover:underline"
            >
              Delete
            </button>
          )}
        </section>
      </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
